'use client';

import { useState, useMemo } from 'react';
import {
  useDeleteFileMutation,
  useGetFileHistoryQuery,
  useUpdateFileStatusMutation,
} from '@/store/api/fileHistoryApi';
import { ExternalLink, Download, MoreVerticalIcon } from 'lucide-react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { toast } from 'react-toastify';

const folderCategories = [
  { label: 'All', value: 'all' },
  { label: 'Tech Data', value: 'tech-data' },
  { label: 'Semi Tech', value: 'semi-tech-data' },
  { label: 'Non Tech', value: 'non-tech-data' },
];
export default function FileHistoryPage() {
  const [folder, setFolder] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [openDelete, setOpenDelete] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 7,
  });
  const { data, isLoading } = useGetFileHistoryQuery({
    category: folder,
    fromDate,
    toDate,
    skip: paginationModel.page * paginationModel.pageSize,
    take: paginationModel.pageSize,
  });

  const [updateStatus] = useUpdateFileStatusMutation();
  const [deleteFile] = useDeleteFileMutation();

  const processedFiles = useMemo(() => {
    if (!data?.data) return [];
    return data.data;
  }, [data]);

  const handleMenuOpen = (event: any, row: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // setSelectedRow(null);
  };

  const handleToggleStatus = async () => {
    const newStatus = selectedRow.status === 'Active' ? 'Inactive' : 'Active';

    await updateStatus({
      id: selectedRow.id,
      status: newStatus,
    });
    toast.success(`Status updated to ${newStatus}`);
    handleMenuClose();
  };

  // const handleDelete = async () => {
  //   await deleteFile(selectedRow.id);
  //   toast.success('File deleted successfully');
  //   handleMenuClose();
  // };
  const handleDelete = async () => {
    if (!selectedRow) return;

    try {
      await deleteFile(selectedRow.id).unwrap();
      toast.success('File deleted successfully');
    } catch (error) {
      toast.error('Delete failed');
    }

    setOpenDelete(false);
  };
  const rows = processedFiles.map((file, index) => ({
    id: file.id,
    serial: paginationModel.page * paginationModel.pageSize + index + 1,
    name: file.name,
    url: file.url,
    status: file.status,
  }));

  const columns: GridColDef[] = [
    { field: 'serial', headerName: 'S.No', width: 80 },

    {
      field: 'name',
      headerName: 'Doc Name',
      flex: 1,
    },

    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => {
        const isActive = params.row.status === 'Active';

        return (
          <span
            style={{
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500,
              backgroundColor: isActive ? '#e6f4ea' : '#fdecea',
              color: isActive ? '#2e7d32' : '#d32f2f',
            }}
          >
            {params.row.status}
          </span>
        );
      },
    },

    {
      field: 'preview',
      headerName: 'Preview',
      width: 100,
      renderCell: (params) => (
        <IconButton
          onClick={() =>
            window.open(
              `https://docs.google.com/gview?url=${params.row.url}&embedded=true`,
              '_blank'
            )
          }
        >
          <ExternalLink size={20} />
        </IconButton>
      ),
    },

    {
      field: 'download',
      headerName: 'Download',
      width: 100,
      renderCell: (params) => (
        <IconButton
          onClick={async () => {
            try {
              const response = await fetch(params.row.url);
              const blob = await response.blob();

              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');

              link.href = url;
              link.download = params.row.name; // file name
              document.body.appendChild(link);
              link.click();

              link.remove();
              window.URL.revokeObjectURL(url);
            } catch (error) {
              console.error('Download failed', error);
            }
          }}
        >
          <Download size={20} />
        </IconButton>
      ),
    },

    {
      field: 'action',
      headerName: 'Action',
      width: 80,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVerticalIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">File History</h1>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <select
          value={folder}
          onChange={(e) => setFolder(e.target.value)}
          className="text-xs px-3 py-2 border rounded-lg"
        >
          {folderCategories.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label.toUpperCase()}
            </option>
          ))}
        </select>

        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="text-xs px-3 py-2 border rounded-lg"
        />

        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="text-xs px-3 py-2 border rounded-lg"
        />
      </div>

      {/* Table */}
      <div style={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          rowCount={data?.count || 0} // ✅ total rows from backend
          paginationMode="server" // ✅ VERY IMPORTANT
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[7, 10, 20]}
          loading={isLoading}
          disableRowSelectionOnClick
        />

        {/* Action Menu */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleToggleStatus}>
            {selectedRow?.status === 'Active' ? ' Inactive' : ' Active'}
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleMenuClose(); // close menu first

              setTimeout(() => {
                setOpenDelete(true); // open popup after menu closes
              }, 0);
            }}
            style={{ color: 'red' }}
          >
            Delete
          </MenuItem>
        </Menu>
      </div>
      {openDelete && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={() => setOpenDelete(false)}
        >
          <div
            className="bg-white p-5 rounded-lg w-[350px] space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-red-600">Confirm Delete</h2>

            <p className="text-sm text-gray-600">Are you sure you want to delete this file?</p>

            <div className="flex justify-end gap-2">
              <button onClick={() => setOpenDelete(false)} className="px-3 py-1 border rounded">
                Cancel
              </button>

              <button onClick={handleDelete} className="px-3 py-1 bg-red-600 text-white rounded">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {isLoading && <p className="text-sm text-gray-500 mt-3">Loading...</p>}
    </div>
  );
}
